import { useState } from 'react';

import {
  useAuthorizeContract,
  useContract,
  useResidualMetrics,
  useShareMetrics,
  useShows,
} from '../../api/hooks';
import { Button, useToast } from '../../components/DesignSystem';
import { errorMessage } from '../../lib/errors';
import { formatNumber, formatPercent } from '../../lib/format';

interface StepReviewApproveProps {
  contractId: string;
  onBack: () => void;
  onAuthorized: () => void;
}

/** Step 4 — review primary terms and approve the contract. */
export function StepReviewApprove({ contractId, onBack, onAuthorized }: StepReviewApproveProps) {
  const { showToast } = useToast();
  const contract = useContract(contractId);
  const shows = useShows({ limit: 100 });
  const shareMetrics = useShareMetrics(contractId);
  const residualMetrics = useResidualMetrics(contractId);

  const authorizeContract = useAuthorizeContract();
  const [isAuthorizing, setIsAuthorizing] = useState(false);

  const show = shows.data?.data.find((entry) => entry.id === contract.data?.showId);
  const season = show?.seasons.find((entry) => entry.id === contract.data?.seasonId);

  async function handleAuthorize() {
    if (isAuthorizing) {
      return;
    }

    setIsAuthorizing(true);

    try {
      await authorizeContract.mutateAsync(contractId);
      showToast('Primary terms approved');
      onAuthorized();
    } catch (error) {
      showToast(errorMessage(error, 'Could not approve primary terms'), 'error');
    } finally {
      setIsAuthorizing(false);
    }
  }

  return (
    <>
      <section className="rfp-card">
        <div className="rfp-card__head">
          <div>
            <h2 className="rfp-card__title">Approval</h2>
            <p className="rfp-card__caption">
              Review all the primary terms above. Once approved, they will be saved to the Approved
              Terms section where you can search and view them by show and season.
            </p>
          </div>
        </div>

        <dl className="rfp-summary">
          <div className="rfp-summary__row">
            <dt>Contract</dt>
            <dd>{contract.data?.title ?? '—'}</dd>
          </div>
          <div className="rfp-summary__row">
            <dt>Type</dt>
            <dd>{contract.data?.contractType === 'secondary' ? 'Secondary' : 'Primary'}</dd>
          </div>
          <div className="rfp-summary__row">
            <dt>Show / Season</dt>
            <dd>
              {show?.name ?? '—'}
              {season ? ` / ${season.seasonCode}` : ''}
            </dd>
          </div>
          <div className="rfp-summary__row">
            <dt>Reported Currency</dt>
            <dd>{contract.data?.reportedCurrency ?? '—'}</dd>
          </div>
          <div className="rfp-summary__row">
            <dt>Contract Distribution Commission</dt>
            <dd>{formatPercent(contract.data?.distributionCommissionPct)}</dd>
          </div>
          <div className="rfp-summary__row">
            <dt>Contract Distribution Expenses</dt>
            <dd>{formatPercent(contract.data?.distributionExpensePct)}</dd>
          </div>
          <div className="rfp-summary__row">
            <dt>Shares Allocated</dt>
            <dd>
              {formatPercent(shareMetrics.data?.totalPercentAllocated)} across{' '}
              {formatNumber(shareMetrics.data?.partyCount)} parties
            </dd>
          </div>
          <div className="rfp-summary__row">
            <dt>Residuals</dt>
            <dd>
              {formatNumber(residualMetrics.data?.count)} entries,{' '}
              {formatPercent(residualMetrics.data?.totalRatePercent)} total rate
            </dd>
          </div>
        </dl>
      </section>

      <div className="rfp-wizard-actions">
        <Button onClick={onBack} type="button" variant="secondary">
          Back
        </Button>
        <Button disabled={isAuthorizing} onClick={handleAuthorize} type="button">
          {isAuthorizing ? 'Approving…' : 'Approve Primary Terms'}
        </Button>
      </div>
    </>
  );
}
